import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  MeasurementDataRangeInternal,
  PersistedMeterAggregatedMeasurementsQueryVariables,
  Query,
  TariffKindInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { PERSISTED_METER_AGGREGATED_MEASUREMENTS } from '../../../../../../../data-access/queries/meter';
import { MeasurementRange } from '../../../../../../../types/measurement';
import { dataPickerFormat, getDateRangeByPage } from '../../../../../../../utils/charts/charts';
import { convertDateToFormat, convertDateToString } from '../../../../../../../utils/helpers/date-convert';
import { ChannelType } from '../../../../../types/channel';
import { TariffName } from '../../../optimizer/configuration/types/tariff-kind';
import { tariffKind } from '../../../optimizer/configuration/utils/tariff-kind';
import { currentFormat } from '../../analysis/utils/helpers';
import { generateEmptyData } from '../../analysis/utils/range';
import { useMeterDetailsContext } from '../../context';

export const useChartComparator = (channel: ChannelType) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallationId } = useInstallationContext();
  const { comparatorTariffs, setComparatorTariffs, comparatorPage, comparatorParam } = useMeterDetailsContext();
  const [getPersistedAggregatedMeasurements, { data, loading }] = useLazyQuery<
    Query,
    PersistedMeterAggregatedMeasurementsQueryVariables
  >(PERSISTED_METER_AGGREGATED_MEASUREMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const operatorOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        Object.values(tariffKind)
          .map((t) => t.operator)
          .filter(Boolean),
      ),
    );
    return unique
      .filter((x) => x !== 'custom')
      .map((op) => ({
        label: op.toUpperCase(),
        value: op,
      }))
      .filter((op) => {
        const allTariffs = Object.entries(tariffKind)
          .filter(([, t]) => t.operator === op.value && t.tariff)
          .map(([enumValue]) => enumValue);
        const selectedTariffs = comparatorTariffs.filter((r) => r.operator === op.value).map((r) => r.tariff);
        return selectedTariffs.length < allTariffs.length || comparatorTariffs.some((r) => r.operator === op.value);
      });
  }, [comparatorTariffs]);

  const getTariffOptions = (operator: string, idx: number) => {
    let allTariffs = Object.entries(tariffKind)
      .filter(([, t]) => t.operator === operator && t.tariff)
      .map(([enumValue, tr]) => ({
        label:
          tr.tariff.includes('dynamic') || tr.tariff.includes(TariffName.G13S)
            ? t(`optimizer.configuration.tariff.${tr.tariff}`).toUpperCase()
            : tr.tariff.toUpperCase(),
        value: enumValue,
        tariffName: tr.tariff,
      }));

    return allTariffs.filter(
      (option) =>
        !comparatorTariffs.some((r, rIdx) => rIdx !== idx && r.operator === operator && r.tariff === option.value),
    );
  };

  const fromTo = useMemo(() => getDateRangeByPage(MeasurementRange.DayInHours, comparatorPage), [comparatorPage]);

  const compareCharts = () => {
    const tariffs = comparatorTariffs
      .map((row) => {
        const found = Object.entries(tariffKind).find(
          ([enumValue, t]) => t.operator === row.operator && enumValue === row.tariff,
        );
        return found ? (found[0] as TariffKindInternal) : undefined;
      })
      .filter((enumValue): enumValue is TariffKindInternal => Boolean(enumValue));

    getPersistedAggregatedMeasurements({
      variables: {
        installationId: selectedInstallationId,
        input: {
          channelId: channel.id,
          deviceId: channel.deviceId,
          indices: [0],
          aggregateVectorRequestItems: [
            {
              type: comparatorParam,
              range: MeasurementDataRangeInternal.InHours,
            },
          ],
          from: fromTo.from,
          to: fromTo.to,
          tariffs,
        },
      },
    });
  };

  useEffect(() => {
    compareCharts();
  }, [fromTo]);

  const handleChange = (idx: number, field: 'operator' | 'tariff', value: string) => {
    setComparatorTariffs((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (field === 'operator') {
          return { operator: value, tariff: '' };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  const handleAddRow = () => {
    setComparatorTariffs((prev) => [...prev, { operator: '', tariff: '' }]);
  };

  const handleRemoveRow = (idx: number) => {
    setComparatorTariffs((prev) => prev.filter((_, i) => i !== idx));
  };

  const chartData = useMemo(() => {
    const items = data?.persistedMeterAggregatedMeasurements.data?.items || [];
    const allDatesFromRange = generateEmptyData(MeasurementRange.DayInHours, fromTo);

    const result = allDatesFromRange.map((date) => {
      const foundDataPerDate = items.find(
        (x) =>
          convertDateToString(new Date(x.dateAtUtc).getTime(), currentFormat(false, MeasurementRange.DayInHours)) ===
          date.time,
      );

      const measurements: Record<string, Record<number, number>> = foundDataPerDate?.measurements || {};
      const formattedMeasurements = Object.entries(measurements).reduce((acc, [key, value]) => {
        if (!key.includes('@')) return acc;

        const cleanKey = key.split('@')[1].replaceAll('_', ' ');
        acc[cleanKey] = value['0'];
        return acc;
      }, {} as Record<string, number>);

      return {
        hour: date.time,
        ...formattedMeasurements,
      };
    });

    return result;
  }, [data, comparatorPage, fromTo]);

  const range = useMemo(() => {
    const currentPage = Math.abs(comparatorPage);
    const date = new Date();
    date.setDate(date.getDate() - currentPage);

    return convertDateToFormat(date, dataPickerFormat[MeasurementRange.DayInHours].format[0]);
  }, [comparatorPage]);

  const emptyTariffs = useMemo(() => {
    const items = data?.persistedMeterAggregatedMeasurements?.data?.items || [];

    const result: Record<string, number[]> = {};

    for (const item of items) {
      const measurements = item.measurements || {};

      for (const key in measurements) {
        if (key.includes(TariffKindInternal.TauronG13S) || key.includes('_DYNAMIC')) return;

        const [, tariff] = key.split('@');
        const value = measurements[key]['0'];

        if (!result[tariff]) {
          result[tariff] = [];
        }

        result[tariff].push(value);
      }
    }

    return !!Object.entries(result).some(([, values]) => values.every((v) => v === 0));
  }, [data]);

  return {
    operatorOptions,
    getTariffOptions,
    compareCharts,
    loading: loading && !data,
    handleAddRow,
    handleChange,
    handleRemoveRow,
    chartData,
    range,
    emptyTariffs,
  };
};
